import { Copy } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/v2/button";
import { VACANCY_OVERRIDE } from "@/features/segments/builder/constants";
import type { SegmentCopyOptions } from "@/features/segments/builder/schemas";
import { ActionTooltip } from "@/features/segments/components/ActionTooltip";
import { DuplicateSegmentDialog } from "@/features/segments/components/DuplicateSegmentDialog";
import type { SegmentDetailResponse } from "@/features/segments/types";

type DuplicateSegmentActionProps = {
  segment: Pick<SegmentDetailResponse, "id" | "name" | "rules" | "vacancyOverrides">;
  clientId?: string;
  variant: "button" | "icon";
  disabledReason?: string;
  onDuplicated?: () => void;
};

export function DuplicateSegmentAction({
  segment,
  clientId,
  variant,
  disabledReason,
  onDuplicated,
}: DuplicateSegmentActionProps) {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const copyable: SegmentCopyOptions = {
    rules: segment.rules.length > 0,
    includedVacancies: segment.vacancyOverrides.some(
      (override) => override.type === VACANCY_OVERRIDE.INCLUDE,
    ),
    excludedVacancies: segment.vacancyOverrides.some(
      (override) => override.type === VACANCY_OVERRIDE.EXCLUDE,
    ),
  };
  const isEmpty = !Object.values(copyable).some(Boolean);
  const reason =
    disabledReason ?? (isEmpty ? t("admin.segments.duplicate.empty") : undefined);
  const isDisabled = reason !== undefined;
  const label = t("admin.segments.duplicate.button");

  return (
    <>
      <ActionTooltip tooltip={reason ?? label} disabled={isDisabled}>
        {variant === "icon" ? (
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            disabled={isDisabled}
            aria-label={label}
            onClick={() => setIsDialogOpen(true)}
          >
            <Copy />
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            disabled={isDisabled}
            onClick={() => setIsDialogOpen(true)}
          >
            <Copy />
            {label}
          </Button>
        )}
      </ActionTooltip>
      <DuplicateSegmentDialog
        segment={segment}
        copyable={copyable}
        clientId={clientId}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onDuplicated={onDuplicated}
      />
    </>
  );
}
